import React, { useCallback, useEffect, useState } from "react";
import { C } from "../constants.js";
import { I } from "../icons.jsx";
import { apiFetch } from "../api.js";
import { Badge } from "../components/Badge.jsx";
import { Spinner } from "../components/Spinner.jsx";
import { formatAuditTime, EVENT_TYPE_CONFIG } from "../utils.js";

export function AuditLogPage({ onMatterClick }) {
  const [entries, setEntries] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [filterType, setFilterType] = useState("");
  const [searchText, setSearchText] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [offset, setOffset] = useState(0);
  const limit = 50;

  const loadEntries = useCallback((currentOffset) => {
    setLoading(true);
    let url = `/api/audit-log?limit=${limit}&offset=${currentOffset}`;
    if (filterType) url += `&event_type=${filterType}`;
    if (dateFrom) url += `&date_from=${dateFrom}`;
    if (dateTo) url += `&date_to=${dateTo}`;
    apiFetch(url)
      .then(data => {
        if (currentOffset === 0) {
          setEntries(data.data || []);
        } else {
          setEntries(prev => [...prev, ...(data.data || [])]);
        }
        setTotalCount(data.total_count || 0);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, [filterType, dateFrom, dateTo]);

  useEffect(() => {
    setOffset(0);
    loadEntries(0);
  }, [filterType, dateFrom, dateTo, loadEntries]);

  const loadMore = () => {
    const next = offset + limit;
    setOffset(next);
    loadEntries(next);
  };

  const eventTypes = Object.keys(EVENT_TYPE_CONFIG);

  const filtered = searchText
    ? entries.filter(e =>
        (e.description || "").toLowerCase().includes(searchText.toLowerCase()) ||
        (e.matter_name || "").toLowerCase().includes(searchText.toLowerCase())
      )
    : entries;

  return React.createElement("div", { className:"fade-in", style: { padding:"32px 40px", maxWidth:"1100px" } },
    React.createElement("h1", {
      style: { fontFamily:"'Playfair Display',serif", fontSize:"26px", fontWeight:600, color:C.navy, marginBottom:"8px" }
    }, "Audit Log"),
    React.createElement("p", {
      style: { fontSize:"14px", color:C.textMuted, marginBottom:"24px" }
    }, "Complete activity trail for compliance and operational visibility"),

    // Filter bar
    React.createElement("div", {
      style: {
        display:"flex", gap:"12px", marginBottom:"20px", alignItems:"center", flexWrap:"wrap",
      }
    },
      // Event type dropdown
      React.createElement("select", {
        value: filterType,
        onChange: e => setFilterType(e.target.value),
        style: {
          padding:"8px 12px", borderRadius:"8px", border:`1px solid ${C.warmGray}`,
          fontSize:"13px", color:C.textDark, background:"#fff", cursor:"pointer",
        }
      },
        React.createElement("option", { value: "" }, "All Events"),
        eventTypes.map(t =>
          React.createElement("option", { key: t, value: t }, EVENT_TYPE_CONFIG[t].label)
        ),
      ),

      // Date range
      React.createElement("div", { style: { display:"flex", alignItems:"center", gap:"6px" } },
        React.createElement("span", { style: { fontSize:"12px", color:C.textMuted } }, "From"),
        React.createElement("input", {
          type: "date", value: dateFrom,
          onChange: e => setDateFrom(e.target.value),
          style: {
            padding:"7px 10px", borderRadius:"8px", border:`1px solid ${C.warmGray}`,
            fontSize:"13px", color:C.textDark, background:"#fff",
          }
        }),
        React.createElement("span", { style: { fontSize:"12px", color:C.textMuted } }, "To"),
        React.createElement("input", {
          type: "date", value: dateTo,
          onChange: e => setDateTo(e.target.value),
          style: {
            padding:"7px 10px", borderRadius:"8px", border:`1px solid ${C.warmGray}`,
            fontSize:"13px", color:C.textDark, background:"#fff",
          }
        }),
        (dateFrom || dateTo) && React.createElement("button", {
          onClick: () => { setDateFrom(""); setDateTo(""); },
          style: {
            border:"none", background:"none", cursor:"pointer", color:C.textLight,
            fontSize:"16px", fontWeight:700, padding:"0 4px", lineHeight:1,
          },
          title: "Clear dates",
        }, "\u00d7"),
      ),

      // Search
      React.createElement("div", { style: { position:"relative", flex:1, maxWidth:"240px" } },
        React.createElement("input", {
          type: "text", placeholder: "Search...",
          value: searchText,
          onChange: e => setSearchText(e.target.value),
          style: {
            width:"100%", padding:"8px 12px 8px 32px", borderRadius:"8px",
            border:`1px solid ${C.warmGray}`, fontSize:"13px",
          }
        }),
        React.createElement("div", { style: { position:"absolute", left:"10px", top:"50%", transform:"translateY(-50%)", opacity:0.4 } }, I.search),
      ),

      React.createElement("span", {
        style: { fontSize:"13px", color:C.textMuted, marginLeft:"auto" }
      }, totalCount + " total entries"),
    ),

    // Table
    React.createElement("div", {
      style: { background:"#fff", borderRadius:"12px", border:`1px solid ${C.warmGray}`, overflow:"hidden" }
    },
      // Header row
      React.createElement("div", {
        style: {
          display:"grid", gridTemplateColumns:"170px 155px 1fr 120px 170px", columnGap:"12px",
          padding:"12px 20px", background:C.warmWhite, borderBottom:`1px solid ${C.warmGray}`,
          fontSize:"11px", fontWeight:600, color:C.textMuted, textTransform:"uppercase", letterSpacing:"0.05em",
        }
      },
        React.createElement("span", null, "Time"),
        React.createElement("span", null, "Event"),
        React.createElement("span", null, "Description"),
        React.createElement("span", null, "User"),
        React.createElement("span", null, "Matter"),
      ),

      // Rows
      loading && entries.length === 0
        ? React.createElement("div", { style: { textAlign:"center", padding:"40px" } }, React.createElement(Spinner, { size: 24 }))
        : filtered.length === 0
          ? React.createElement("div", { style: { textAlign:"center", padding:"40px", fontSize:"14px", color:C.textMuted } }, "No entries found")
          : filtered.map(e => {
              const cfg = EVENT_TYPE_CONFIG[e.event_type] || { label: e.event_type, variant: "default" };
              return React.createElement("div", {
                key: e.id,
                style: {
                  display:"grid", gridTemplateColumns:"170px 155px minmax(0,1fr) 120px 170px", columnGap:"12px",
                  padding:"12px 20px", borderBottom:`1px solid ${C.warmGray}20`,
                  fontSize:"13px", alignItems:"center",
                },
              },
                React.createElement("span", { style: { color:C.textMuted, fontSize:"12px" } },
                  formatAuditTime(e.created_at),
                ),
                React.createElement("span", null,
                  React.createElement(Badge, { variant: cfg.variant }, cfg.label),
                ),
                React.createElement("span", { style: { color:C.textDark, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap", minWidth:0 } }, e.description),
                React.createElement("span", {
                  style: { fontSize:"12px", color:C.textDark, display:"flex", alignItems:"center", gap:"4px", overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }
                },
                  e.user_name
                    ? React.createElement(React.Fragment, null, I.user, " ", e.user_name)
                    : React.createElement("span", { style: { color:C.textLight } }, "\u2014"),
                ),
                React.createElement("span", null,
                  e.matter_name
                    ? React.createElement("button", {
                        onClick: () => e.matter_id && onMatterClick({ id: parseInt(e.matter_id, 10), display_number: e.matter_name }),
                        style: {
                          border:"none", background:"none", color:C.gold, cursor:"pointer",
                          fontSize:"12px", fontWeight:600, padding:0, textAlign:"left",
                          overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap", maxWidth:"160px", display:"block",
                        }
                      }, e.matter_name)
                    : e.matter_id
                      ? React.createElement("span", { style: { fontSize:"12px", color:C.textLight } }, "#" + e.matter_id)
                      : React.createElement("span", { style: { fontSize:"12px", color:C.textLight } }, "\u2014"),
                ),
              );
            }),
    ),

    // Load More
    entries.length < totalCount && React.createElement("div", {
      style: { textAlign:"center", marginTop:"20px" }
    },
      React.createElement("button", {
        onClick: loadMore,
        disabled: loading,
        style: {
          padding:"10px 28px", borderRadius:"8px", border:`1px solid ${C.warmGray}`,
          background:"#fff", cursor:"pointer", fontSize:"13px", fontWeight:600, color:C.textDark,
          display:"inline-flex", alignItems:"center", gap:"8px",
        }
      },
        loading ? React.createElement(Spinner, { size: 14 }) : null,
        "Load More",
      ),
    ),
  );
}

